import { useCallback, useEffect, useState } from 'react';
import { Modal } from 'antd';
import i18n from '../i18n';
import axiosInstance from '../variables/api/axiosInstance';

/**
 * Avertir avant d'imprimer un document qui devait être signé et ne le sera pas.
 *
 * POURQUOI CET AVERTISSEMENT EXISTE. Les signatures sont temporaires : celle du
 * Secrétaire Exécutif vaut une trentaine de minutes, et peut avoir expiré entre le
 * moment où il l'a déposée et celui où un rédacteur imprime. Sans avertissement, la
 * feuille sort avec un espace vide et l'agent ne s'en aperçoit qu'en la regardant.
 *
 * POURQUOI ELLE NE BLOQUE PAS. Un dossier part parfois signé à la main, et personne
 * ne peut deviner d'ici que ce tirage-là n'a pas besoin de la signature. On dit ce
 * qui manque, l'agent décide.
 *
 * POURQUOI L'ÉTAT EST RELU AU MOMENT DU CLIC. Trente minutes, c'est moins que le temps
 * qu'un écran d'impression reste ouvert. Un état lu à l'affichage aurait annoncé une
 * signature valide sur un document qui sort vierge - précisément le défaut qu'on
 * corrige. Celui du montage ne sert qu'à l'affichage discret dans la barre d'outils.
 */
export function useAvertissementSignature(codeDocument) {
  const [etat, setEtat] = useState(null);

  const lire = useCallback(async () => {
    if (!codeDocument) return null;
    try {
      const res = await axiosInstance.get(
        `/v1/signatures/document/${codeDocument}/etat-impression`,
        { skipErrorToast: true },
      );
      return res?.data?.data || null;
    } catch {
      /* Un échec ne doit pas empêcher d'imprimer. Sans réponse, on se tait : avertir
         « la signature manque peut-être » à chaque incident réseau apprendrait à
         passer outre l'avertissement sans le lire. */
      return null;
    }
  }, [codeDocument]);

  useEffect(() => {
    let ignore = false;
    lire().then((e) => { if (!ignore) setEtat(e); });
    return () => { ignore = true; };
  }, [lire]);

  const confirmerPuisImprimer = useCallback(async (imprimer) => {
    const frais = await lire();
    setEtat(frais);
    if (!frais?.signatureRequise || frais.signatureDisponible) {
      imprimer();
      return;
    }
    Modal.confirm({
      title: i18n.t('commun:avertissementSignature.titre'),
      content: frais.message,
      okText: i18n.t('commun:avertissementSignature.imprimerQuandMeme'),
      cancelText: i18n.t('commun:avertissementSignature.annuler'),
      onOk: imprimer,
    });
  }, [lire]);

  return { etat, confirmerPuisImprimer };
}

/**
 * Charge la signature d'un document AVANT de produire son HTML.
 *
 * POURQUOI CETTE FONCTION EXISTE. Quatre documents - ordre de dépense, quittance,
 * note de mission, note à la victime - ne s'impriment pas depuis le DOM de la page :
 * ils sont sérialisés par `renderToStaticMarkup`, puis injectés dans une fenêtre ou
 * une iframe. Aucun effet React ne s'y exécute, donc `BlocSignature` n'y déclenchait
 * jamais son chargement et ces documents sortaient sans signature, quel que soit le
 * paramétrage. La signature doit donc être en main AVANT le rendu, et voyager
 * comme une donnée ordinaire.
 *
 * POURQUOI UNE `data:` ET NON UNE `blob:`. Le rendu produit une CHAÎNE de caractères.
 * Une URL `blob:` n'est qu'un pointeur vers la mémoire du document parent : sérialisée
 * puis relue dans une autre fenêtre, elle pointe vers un objet que rien ne garantit
 * d'atteindre, et que `revokeObjectURL` peut avoir libéré entre-temps. Une `data:`
 * porte les octets avec elle - c'est la seule forme qui survive au voyage.
 *
 * Rend `null` dès qu'une signature n'est pas due : l'espace reste vide et le document
 * sort comme avant, sans erreur ni blocage.
 */
export async function chargerSignatureImprimable(codeDocument, sinistreTrackingId) {
  if (!codeDocument) return null;
  try {
    const reponse = await axiosInstance.get(`/v1/signatures/document/${codeDocument}`, {
      params: sinistreTrackingId ? { sinistre: sinistreTrackingId } : undefined,
      responseType: 'blob',
      skipErrorToast: true,
    });
    const image = await blobEnDataUrl(reponse.data);
    if (!image) return null;

    /* L'identité est accessoire : mieux vaut une signature sans nom qu'un document
       qui refuse de sortir parce qu'un second appel a échoué. */
    let signataire = null;
    try {
      const r = await axiosInstance.get(
        `/v1/signatures/document/${codeDocument}/signataire`,
        { skipErrorToast: true },
      );
      signataire = r?.data?.data || null;
    } catch {
      signataire = null;
    }

    return { image, nom: signataire?.nom || null, fonction: signataire?.fonction || null };
  } catch {
    // Les trois refus du serveur sont indiscernables, et c'est voulu : ici, tous
    // donnent le même résultat - pas de signature, document inchangé.
    return null;
  }
}

function blobEnDataUrl(blob) {
  return new Promise((resoudre) => {
    if (!blob) { resoudre(null); return; }
    const lecteur = new FileReader();
    lecteur.onloadend = () => resoudre(typeof lecteur.result === 'string' ? lecteur.result : null);
    lecteur.onerror = () => resoudre(null);
    lecteur.readAsDataURL(blob);
  });
}

/**
 * Avertit, puis imprime - pour les documents rendus hors du DOM de la page.
 *
 * Même règle que `useAvertissementSignature`, mais sous forme de fonction : ces
 * documents s'impriment depuis un gestionnaire de clic, sans composant monté qui
 * pourrait porter un état.
 */
export async function confirmerImpressionSansSignature(codeDocument, imprimer) {
  let etat = null;
  try {
    const res = await axiosInstance.get(
      `/v1/signatures/document/${codeDocument}/etat-impression`,
      { skipErrorToast: true },
    );
    etat = res?.data?.data || null;
  } catch {
    etat = null;
  }
  if (!etat?.signatureRequise || etat.signatureDisponible) {
    await imprimer();
    return;
  }
  Modal.confirm({
    title: i18n.t('commun:avertissementSignature.titre'),
    content: etat.message,
    okText: i18n.t('commun:avertissementSignature.imprimerQuandMeme'),
    cancelText: i18n.t('commun:avertissementSignature.annuler'),
    onOk: imprimer,
  });
}
